import { useMemo } from "react";

import { type ResourceConfig, ResourcePage, fmtDate } from "@/components/resource-page";
import { CITY_FIELDS } from "@/pages/CitiesPage";
import { PROVINCE_FIELDS } from "@/pages/ProvincesPage";
import type { Barangay } from "@/utils/geo";
import type { TranslationKey } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

export const barangaysConfig = (
  t: (key: TranslationKey) => string,
): ResourceConfig<Barangay> => ({
  title: t("geo.barangays"),
  singular: t("geo.barangay"),
  plural: t("geo.barangays"),
  endpoint: "/barangays",
  model: "barangay",
  // The two halves of the same door. A barangay names its city; the province
  // column is only needed where two provinces have a city of the same name.
  exportable: true,
  // Written by the server, from the columns ticked in the dialog: the file
  // is every matching barangay, and the browser only ever holds a page of them.
  exportServer: { permission: "view__barangay" },
  importable: {
    columns: ["Name", "Code", "Province", "City", "Population", "Latitude", "Longitude"],
    template: "/templates/barangays-import-sample.csv",
    file: "barangays-import-sample.csv",
    permission: "add__barangay",
  },
  // Deleting a place is never what anyone means: the members, clubs and
  // listings recorded against it still name it. Delete puts the row in the
  // archive, and the archive gives it back.
  archiveFilter: { name: "archived", value: "true", permission: "delete__barangay" },
  // Tick the rows, then one button above them: Delete on the list,
  // Restore in the archive. Behind the same right as deleting one.
  bulk: { permission: "delete__barangay" },
  restore: { permission: "delete__barangay" },
  confirmDeleteMessage: t("geo.confirmArchive"),
  columns: [
    {
      key: "id",
      label: t("rp.id"),
      sortKey: "id",
      render: (row) => (
        <span className="text-accent underline underline-offset-4">{row.id}</span>
      ),
    },
    { key: "name", label: t("geo.name"), sortKey: "name", render: (row) => row.name },
    {
      key: "code",
      label: t("geo.code"),
      sortKey: "code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    { key: "city", label: t("geo.city"), render: (row) => row.city_name ?? "—" },
    { key: "province", label: t("geo.province"), render: (row) => row.province_name ?? "—" },
    {
      key: "population",
      label: t("geo.population"),
      sortKey: "population",
      render: (row) => row.population ?? "—",
    },
    { key: "latitude", label: t("geo.latitude"), render: (row) => row.latitude ?? "—" },
    { key: "longitude", label: t("geo.longitude"), render: (row) => row.longitude ?? "—" },
    {
      key: "created_by",
      label: t("geo.createdBy"),
      render: (row, lookup) => lookup("created_by_id", row.created_by_id),
    },
    {
      key: "created_at",
      label: t("geo.createdAt"),
      sortKey: "created_at",
      render: (row) => fmtDate(row.created_at),
    },
  ],
  defaultSort: { key: "id", order: "desc" },
  // The Employees row: a small search and a funnel on the left, the screen's
  // own actions as icons on the right.
  toolbarVariant: "compact",
  // Add, view and edit as sheets over the list, the way Employees does it:
  // these records are short, and the table behind is the context for them.
  formVariant: "modal",
  viewVariant: "modal",
  searchPlaceholder: t("rp.searchShort"),
  filters: [
    {
      name: "created_by_id",
      label: t("geo.createdByFilter"),
      type: "fk",
      endpoint: "/users/compact",
    },
    { name: "created_from", label: t("geo.createdFrom"), type: "date" },
    { name: "created_to", label: t("geo.createdTo"), type: "date" },
    {
      name: "province_id",
      label: t("geo.province"),
      type: "fk",
      endpoint: "/provinces/compact",
    },
    {
      name: "city_id",
      label: t("geo.city"),
      type: "fk",
      endpoint: "/cities/compact",
      dependsOn: "province_id",
    },
  ],
  fields: [
    { name: "name", label: t("geo.name"), required: true },
    { name: "code", label: t("geo.code"), required: true },
    // Province is not a column on a barangay; it narrows the city list.
    {
      name: "province_id",
      label: t("geo.province"),
      type: "fk",
      required: true,
      endpoint: "/provinces/compact",
      create: {
        singular: t("geo.province"),
        endpoint: "/provinces",
        fields: PROVINCE_FIELDS(t),
        permission: "add__province",
      },
    },
    {
      name: "city_id",
      label: t("geo.city"),
      type: "fk",
      required: true,
      endpoint: "/cities/compact",
      dependsOn: "province_id",
      // The city a barangay belongs to is the one most often missing, and the
      // form asking for it is the worst moment to have to leave.
      create: {
        singular: t("geo.city"),
        endpoint: "/cities",
        fields: CITY_FIELDS(t),
        permission: "add__city",
      },
    },
    { name: "population", label: t("geo.population"), type: "number", min: 0, required: true },
    {
      name: "latitude",
      required: true,
      label: t("geo.latitude"),
      type: "number",
      step: "any",
      min: -90,
      max: 90,
    },
    {
      name: "longitude",
      required: true,
      label: t("geo.longitude"),
      type: "number",
      step: "any",
      min: -180,
      max: 180,
    },
  ],
  detailLabels: {
    population: t("geo.population"),
    latitude: t("geo.latitude"),
    longitude: t("geo.longitude"),
    code: t("geo.code"),
    city_name: t("geo.city"),
    province_name: t("geo.province"),
  },
  toForm: (row) => ({
    name: row.name,
    code: row.code,
    province_id: String(row.province_id),
    city_id: String(row.city_id),
    population: row.population?.toString() ?? "",
    latitude: row.latitude?.toString() ?? "",
    longitude: row.longitude?.toString() ?? "",
  }),
});

function BarangaysPage() {
  const t = useTranslation();
  const config = useMemo(() => barangaysConfig(t), [t]);
  return <ResourcePage config={config} />;
}

export default BarangaysPage;
